import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import Tree from "react-d3-tree";
import { PawPrintIcon as PawPrint, GitForkIcon as GitFork, ClockCounterClockwiseIcon as ClockCounterClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";

/** Shape: { name, breed, sex, registration_number, photo, titles, sire, dam, tentative? } */
function treeToD3(node) {
  if (!node) return null;
  const children = [];
  const s = treeToD3(node.sire);
  if (s) children.push({ ...s, _relation: "Sire" });
  const d = treeToD3(node.dam);
  if (d) children.push({ ...d, _relation: "Dam" });
  return {
    name: node.name || "Unknown",
    attributes: {
      breed: node.breed || "",
      regNum: node.registration_number || "",
    },
    _id: node.id,
    _sex: node.sex || "",
    _titles: Array.isArray(node.titles) ? node.titles : [],
    _tentative: !!node.tentative,
    children,
  };
}

const TentativeNode = ({ nodeDatum }) => {
  const tentative = nodeDatum._tentative;
  const sexColor = nodeDatum._sex === "male" ? "#0E0E0E" : "#C1272D";
  const stripe = tentative ? "#C9A227" : sexColor;
  return (
    <g>
      <foreignObject x={-110} y={-40} width={220} height={80}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            background: tentative ? "#FBF7EC" : "white",
            border: tentative ? "1.5px dashed #C9A227" : "1px solid #E5E5E5",
            borderLeft: `4px solid ${stripe}`,
            borderRadius: 12,
            padding: 10,
            boxShadow: "0 8px 30px rgba(90,107,86,0.06)",
            display: "flex",
            gap: 10,
            alignItems: "center",
            fontFamily: "Manrope, sans-serif",
            position: "relative",
          }}
        >
          {tentative && (
            <span
              style={{
                position: "absolute",
                top: -10,
                right: 8,
                background: "#C9A227",
                color: "white",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.1em",
                padding: "2px 6px",
                borderRadius: 999,
                textTransform: "uppercase",
              }}
            >
              Pending
            </span>
          )}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 8,
              overflow: "hidden",
              background: "#FAFAFA",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PawPrint size={18} weight="duotone" color={tentative ? "#C9A227" : "#6B6B6B"} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "Spectral, serif",
                fontSize: 15,
                fontWeight: 500,
                color: tentative ? "#8A6A00" : "#0E0E0E",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nodeDatum.name}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "#6B6B6B",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nodeDatum.attributes?.breed}
              {nodeDatum.attributes?.regNum ? ` · ${nodeDatum.attributes.regNum}` : ""}
            </div>
          </div>
        </div>
      </foreignObject>
    </g>
  );
};

/**
 * Modal that previews the pedigree of a pending Pedigree Addition with its
 * tentative ancestors visually folded into the tree (gold-dashed cards).
 *
 * Used by:
 *   - AdminPedigreeAdditions: each row's "Preview" button
 *   - DogDetail "pending pedigree" note: "View tentative pedigree" link
 *   - User's pending submissions list (future)
 */
export default function PedigreeAdditionPreview({ open, onClose, additionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTree, setShowTree] = useState(false);

  useEffect(() => {
    if (!open || !additionId) {
      setShowTree(false);
      return;
    }
    setLoading(true);
    api
      .get(`/pedigree-additions/${additionId}/tree`, { params: { generations: 4 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, additionId]);

  // Mount the tree after the dialog finishes its open transition so
  // react-d3-tree can read non-zero parent dimensions.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setShowTree(true), 220);
    return () => clearTimeout(t);
  }, [open]);

  const tree = data?.tree ? treeToD3(data.tree) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setData(null); } }}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="pedigree-addition-preview"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitFork size={18} weight="duotone" className="text-gold" />
            Tentative pedigree preview
            {data?.status === "pending_admin_approve" && (
              <Badge className="bg-[#C9A227]/20 text-[#8A6A00] border-[#C9A227]/40 hover:bg-[#C9A227]/20">
                <ClockCounterClockwise size={11} className="mr-1" />
                Pending admin
              </Badge>
            )}
            {data?.status === "revisions_requested" && (
              <Badge className="bg-[#C9A227]/20 text-[#8A6A00] border-[#C9A227]/40 hover:bg-[#C9A227]/20">
                Revisions requested
              </Badge>
            )}
            {data?.status === "rejected" && (
              <Badge className="bg-[#C1272D]/10 text-crimson border-[#C1272D]/30 hover:bg-[#C1272D]/10">
                Rejected
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {data ? (
              <>
                Submitted by <span className="font-semibold text-ink">{data.submitted_by_name || "—"}</span>{" "}
                · root dog: <span className="font-semibold text-ink">{data.attached_dog_name}</span>
                {" · "}
                <span className="font-mono">{data.tentative_count}</span> tentative ancestor{data.tentative_count === 1 ? "" : "s"}
              </>
            ) : (
              "Loading the merged pedigree…"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 text-[11px] text-[#6B6B6B] mt-2">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-[#E5E5E5] bg-white" />
            Existing dog
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border-[1.5px] border-dashed border-[#C9A227] bg-[#FBF7EC]" />
            Tentative (in this submission)
          </span>
        </div>

        {data?.revision_notes && (
          <div
            className="mt-3 bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-3"
            data-testid="preview-revision-notes"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A6A00]">
              Admin requested revisions
              {data.revisions_requested_by_name && (
                <span className="font-normal normal-case ml-1">
                  · {data.revisions_requested_by_name}
                </span>
              )}
            </p>
            <p className="text-sm text-ink mt-1 whitespace-pre-wrap">{data.revision_notes}</p>
          </div>
        )}
        {data?.rejection_reason && (
          <div className="mt-3 bg-[#C1272D]/5 border border-[#C1272D]/30 rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-crimson">Rejected</p>
            <p className="text-sm text-ink mt-1 whitespace-pre-wrap">{data.rejection_reason}</p>
          </div>
        )}

        <div
          className="mt-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl overflow-hidden"
          style={{ height: 520 }}
          data-testid="pedigree-addition-preview-tree"
        >
          {loading ? (
            <div className="h-full flex items-center justify-center text-[#6B6B6B]">Loading…</div>
          ) : tree && showTree ? (
            <Tree
              data={tree}
              orientation="horizontal"
              translate={{ x: 160, y: 240 }}
              pathFunc="step"
              collapsible={false}
              separation={{ siblings: 1.2, nonSiblings: 1.5 }}
              nodeSize={{ x: 260, y: 100 }}
              renderCustomNodeElement={(rd3tProps) => (
                <TentativeNode nodeDatum={rd3tProps.nodeDatum} />
              )}
              zoom={0.85}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-[#6B6B6B]">
              {tree ? "Rendering tree…" : "No tree to display."}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
